import { useEffect, useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Modal from '../ui/Modal'
import { api, ApiError } from '../../lib/api'
import {
  LegalAreaLabel,
  NON_LITIGATION_AREAS,
  type CaseDetail,
  type ClientListItem,
  type ClientListResponse,
  type CreateCaseInput,
  type LegalArea,
} from '@toga/shared'
import IssuerSelect from '../billing/IssuerSelect'

interface Props {
  onClose: () => void
  onCreated: (detail: CaseDetail) => void
  defaultClientId?: string
}

export default function NewCaseModal({ onClose, onCreated, defaultClientId }: Props) {
  const navigate = useNavigate()
  const [clients, setClients] = useState<ClientListItem[] | null>(null)
  const [clientId, setClientId] = useState(defaultClientId ?? '')
  const [title, setTitle] = useState('')
  const [area, setArea] = useState<LegalArea>('CIVIL')
  const [description, setDescription] = useState('')
  const [courtName, setCourtName] = useState('')
  const [courtFileNumber, setCourtFileNumber] = useState('')
  const [opposingParty, setOpposingParty] = useState('')
  const [issuerId, setIssuerId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const resp = await api.get<ClientListResponse>('/clients?pageSize=100')
        setClients(resp.items)
        if (!defaultClientId && resp.items[0]) setClientId(resp.items[0].id)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'No se pudieron cargar los clientes')
      }
    })()
  }, [defaultClientId])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      // Asesoría: sin componente procesal, no se guardan juzgado ni procedimiento.
      const isAdvisory = NON_LITIGATION_AREAS.includes(area)
      const payload: CreateCaseInput = {
        title,
        clientId,
        area,
        status: 'ABIERTA',
        description: description || null,
        courtName: isAdvisory ? null : courtName || null,
        courtFileNumber: isAdvisory ? null : courtFileNumber || null,
        opposingParty: opposingParty || null,
        issuerId,
      }
      const { case: created } = await api.post<{ case: CaseDetail }>('/cases', payload)
      onCreated(created)
      navigate(`/cases/${created.id}`)
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as
          | { error?: string; issues?: { fieldErrors?: Record<string, string[]> } }
          | undefined
        const fieldErr = body?.issues?.fieldErrors
        const firstFieldErr = fieldErr ? Object.values(fieldErr).flat().find(Boolean) : undefined
        setError(firstFieldErr ?? body?.error ?? 'No se pudo crear el expediente.')
      } else {
        setError('Error de red.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60'

  return (
    <Modal open onClose={onClose} title="Nuevo expediente" maxWidth="max-w-2xl">
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-xs t-muted mb-1.5">Cliente</label>
          {clients === null ? (
            <div className="t-muted text-sm">
              <Loader2 size={14} className="inline animate-spin mr-2" /> Cargando clientes…
            </div>
          ) : clients.length === 0 ? (
            <div className="text-sm text-amber-300">
              No hay clientes todavía. Crea uno primero desde la sección Clientes.
            </div>
          ) : (
            <select
              required
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className={inputCls}
            >
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                  {c.nationalId ? ` · ${c.nationalId}` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">Título / asunto</label>
          <input
            required
            minLength={2}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="P. ej. Reclamación cantidad nóminas pendientes"
            className={inputCls}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1.5">Área</label>
            <select
              value={area}
              onChange={(e) => setArea(e.target.value as LegalArea)}
              className={inputCls}
            >
              {Object.entries(LegalAreaLabel).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">Parte contraria (opcional)</label>
            <input
              value={opposingParty}
              onChange={(e) => setOpposingParty(e.target.value)}
              className={inputCls}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs t-muted mb-1.5">Facturar con (emisor, opcional)</label>
            <IssuerSelect
              value={issuerId}
              onChange={setIssuerId}
              emptyLabel="El del abogado responsable, el del cliente o el del bufete"
              className={inputCls}
            />
          </div>
          {!NON_LITIGATION_AREAS.includes(area) && (
            <>
              <div>
                <label className="block text-xs t-muted mb-1.5">Juzgado</label>
                <input
                  value={courtName}
                  onChange={(e) => setCourtName(e.target.value)}
                  placeholder="Juzgado de lo Civil nº 5 de Madrid"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs t-muted mb-1.5">Nº de procedimiento</label>
                <input
                  value={courtFileNumber}
                  onChange={(e) => setCourtFileNumber(e.target.value)}
                  placeholder="PO 1234/2026"
                  className={`${inputCls} font-mono`}
                />
              </div>
            </>
          )}
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">Descripción</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={inputCls}
          />
        </div>

        {error && (
          <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-slate-800/60 hover:bg-slate-800 t-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting || !clientId || !title}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Crear expediente
          </button>
        </div>
      </form>
    </Modal>
  )
}
